/**
 * Turning one frame of a live preview into the bytes the record will hold.
 *
 * **The stored frame is un-mirrored by default, and that is the whole reason this module is written
 * as a pure function with an injectable canvas.** A front-facing camera mirrors its preview, because
 * a person expects to move their left hand and see the left of the screen move; an identity
 * photograph produced from that preview is back-to-front, so a scar, a parting or lettering on a
 * shirt is on the wrong side of the face an operator is comparing against. The preview stays
 * mirrored — taking that away makes the camera unusable — and what is SENT is the camera's own
 * orientation unless somebody deliberately asks otherwise.
 *
 * There is no way to see that defect by looking at the screen: the preview is mirrored either way,
 * and the stored image is only ever seen later, beside a different photograph, by somebody who has
 * no reason to suspect it. So the transform is a value this module returns rather than a CSS class
 * on a video element, and the test asserts the produced BYTES rather than the preview.
 *
 * **Dimensions are decided here rather than left to the camera.** A modern webcam offers anything
 * from 640x480 to 4K; the server re-encodes to 1600 on its longest edge anyway, so sending more is
 * bytes across a desk's network and heap in a process that bounds its own concurrency. The frame is
 * scaled down to that edge, never up: enlarging a 640-pixel camera to 1600 stores a blurred image
 * that claims a resolution it does not have.
 */

/** The longest edge a capture is sent at, matching what the server's pipeline re-encodes to. */
export const CAPTURE_MAX_EDGE = 1600;

/** JPEG quality for a capture. High enough that a re-encode at the server loses nothing visible. */
const CAPTURE_QUALITY = 0.92;

/** The minimum of a 2D context this module uses, so a test can supply one it can inspect. */
export interface CaptureContext {
  translate: (x: number, y: number) => void;
  scale: (x: number, y: number) => void;
  drawImage: (source: CanvasImageSource, dx: number, dy: number, dw: number, dh: number) => void;
}

/** The minimum of a canvas this module uses. */
export interface CaptureCanvas {
  width: number;
  height: number;
  getContext: (contextId: '2d') => CaptureContext | null;
  toDataURL: (type?: string, quality?: number) => string;
}

/** The size a frame is stored at, scaled down to {@link CAPTURE_MAX_EDGE} and never up. */
export function captureSize(
  source: { readonly width: number; readonly height: number },
  maxEdge: number = CAPTURE_MAX_EDGE,
): { readonly width: number; readonly height: number } {
  const longest = Math.max(source.width, source.height);
  if (longest === 0) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(1, maxEdge / longest);
  return {
    width: Math.max(1, Math.round(source.width * scale)),
    height: Math.max(1, Math.round(source.height * scale)),
  };
}

/**
 * The horizontal transform a capture is drawn under.
 *
 * `scaleX: 1` is the camera's own orientation, which is what a stored identity photograph must be.
 * `-1` is the mirrored one, and it is only ever returned when somebody asked for it explicitly.
 */
export function frameTransform(options: {
  readonly storeMirrored: boolean;
  readonly width: number;
}): { readonly scaleX: number; readonly translateX: number } {
  return options.storeMirrored
    ? { scaleX: -1, translateX: options.width }
    : { scaleX: 1, translateX: 0 };
}

/** Everything a capture needs beyond the video element itself. */
export interface CaptureOptions {
  /** The source's natural size, which for a video element is `videoWidth`/`videoHeight`. */
  readonly width: number;
  readonly height: number;
  /**
   * Whether the operator asked for the MIRRORED frame to be stored.
   *
   * `false` is the default everywhere, and the preview's own mirroring is a separate decision that
   * does not reach this function: a control that changed both at once is how a mirrored identity
   * photograph gets stored by somebody who was only trying to see themselves the right way round.
   */
  readonly storeMirrored: boolean;
  readonly canvas: CaptureCanvas;
  readonly quality?: number;
}

/** A `data:` URL with its prefix removed, which is the only form the capture route accepts. */
export function base64Of(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
}

/**
 * One frame, as base64 with no data-URL prefix.
 *
 * The prefix is stripped rather than sent, because `captureImageRequestSchema` refuses it: it is a
 * claim about the format made by the caller, and this system decides a format by sniffing magic
 * bytes. Accepting it would put an authoritative-looking string in the request that nothing reads.
 */
export function captureFrame(source: CanvasImageSource, options: CaptureOptions): string {
  const size = captureSize({ width: options.width, height: options.height });
  const { canvas } = options;
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext('2d');
  if (context === null) {
    throw new TypeError('this browser gave no 2d context for a capture');
  }

  const transform = frameTransform({ storeMirrored: options.storeMirrored, width: size.width });
  context.translate(transform.translateX, 0);
  context.scale(transform.scaleX, 1);
  context.drawImage(source, 0, 0, size.width, size.height);

  return base64Of(canvas.toDataURL('image/jpeg', options.quality ?? CAPTURE_QUALITY));
}
